import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import { Backdrop, Button, Icon, Tooltip } from '../../../components';
import { formatLocationAddressList } from './bulkPricing.utils';

import styles from './bulkPricingConfirmationModal.module.scss';
import { ReactComponent as RedIndicator } from '../../../components/icon/locationsSvg/red.svg';

const ValuePill = ({ value, variant }) => (
  <span className={variant === 'new' ? styles.pillNew : styles.pillOld}>
    {value}
  </span>
);

const BulkPricingConfirmationModal = ({
  rows,
  totalSelectedLocations,
  loading,
  onClose,
  onConfirm,
}) =>
  createPortal(
    <>
      <div className={styles.modal} role="dialog" aria-modal="true">
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Uždaryti"
        >
          <Icon name="close" />
        </button>

        <div className={styles.iconWrap}>
          <Icon name="feedback-error" classes={{ icon: styles.alertIcon }} />
        </div>

        <h2 className={styles.title}>Atnaujinimo patvirtinimas</h2>
        <p className={styles.subtitle}>
          Apačioje pateiktos kainos prieš ir po atnaujinimo.
        </p>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Paslauga</th>
                <th className={styles.colOld}>Sena kaina</th>
                <th />
                <th>Nauja kaina</th>
                <th className={styles.colOld}>Sena trukmė</th>
                <th />
                <th>Nauja trukmė</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const locationLabel = formatLocationAddressList(row.locations);
                const showAllLocations =
                  row.locations.length === totalSelectedLocations &&
                  row.locations.length > 1;

                return (
                  <tr key={`${row.serviceLabel}-${index}`}>
                    <td>
                      <div className={styles.serviceCell}>
                        {(row.priceVaries || row.durationVaries) && (
                          <Tooltip tip="Sena kaina yra skirtinga" align="start">
                            <RedIndicator className={styles.redIndicator} />
                          </Tooltip>
                        )}
                        <div>
                          <p className={styles.serviceName}>
                            {row.serviceLabel}
                          </p>
                          <p className={styles.locationList}>
                            {showAllLocations
                              ? 'Visos lokacijos'
                              : locationLabel}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className={styles.colOld}>
                      <ValuePill value={row.oldPrice} />
                    </td>
                    <td className={styles.arrow}>
                      <Icon name="arrow-right" />
                    </td>
                    <td>
                      <ValuePill value={row.newPrice} variant="new" />
                    </td>
                    <td className={styles.colOld}>
                      <ValuePill value={row.oldDuration} />
                    </td>
                    <td className={styles.arrow}>
                      <Icon name="arrow-right" />
                    </td>
                    <td>
                      <ValuePill value={row.newDuration} variant="new" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={styles.actions}>
          <Button
            color="outline"
            type="button"
            onClick={onClose}
            disabled={loading}
          >
            Uždaryti
          </Button>
          <Button
            color="primary"
            type="button"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Vykdoma...' : 'Patvirtinti'}
          </Button>
        </div>
      </div>
      <Backdrop onCancel={onClose} elevated />
    </>,
    document.body
  );

BulkPricingConfirmationModal.propTypes = {
  totalSelectedLocations: PropTypes.number.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      serviceLabel: PropTypes.string.isRequired,
      locations: PropTypes.array.isRequired,
      oldPrice: PropTypes.string.isRequired,
      newPrice: PropTypes.string.isRequired,
      oldDuration: PropTypes.string.isRequired,
      newDuration: PropTypes.string.isRequired,
      priceVaries: PropTypes.bool,
      durationVaries: PropTypes.bool,
    })
  ).isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

BulkPricingConfirmationModal.defaultProps = {
  loading: false,
};

export default BulkPricingConfirmationModal;
